/**
 * landedCost.js — Pure landed-cost math module.
 *
 * MIRROR of cnerzy-std-parts-backend/src/domain/landedCost.js
 * Keep in sync with the backend. Long-term: factor into a shared package.
 *
 * All functions are stateless. Numbers are computed in INR using Indian customs
 * duty structure: BCD (Basic Customs Duty) + SWS (Social Welfare Surcharge on BCD)
 * + IGST (Integrated Goods and Services Tax on assessable value + BCD + SWS).
 *
 * Formula source: INR Quote Template.xlsx and Global Landed-Cost Template.xlsx.
 * Tests lock byte-equivalence with those spreadsheets.
 *
 * NOTE on assessable value (two template variants):
 *
 * Template 1 (DutyPaid_InvoiceAV / INR Quote Template):
 *   assessable = unitCostUSD × qty × customsFx     (goods only)
 *   landedTotal = assessable + duties + (freight+insurance+additions) × customsFx + miscINR
 *
 * Template 2 (Global Landed-Cost / LandedCostGlobal):
 *   assessable = (unitCostUSD + freightUSD + insuranceUSD) × qty × customsFx
 *   landedTotal = assessable + duties + miscINR   (no extra freight term)
 *
 * Switch via `cifFreightInAssessable: true` for Template 2 behavior.
 */

/**
 * Round to two decimal places (standard commercial rounding).
 */
function r2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Compute full landed-cost build-up for a line in INR.
 */
export function computeLandedCostINR({
  unitCostUSD = 0,
  qty = 0,
  customsFx = 1,
  freightUSD = 0,
  insuranceUSD = 0,
  additionsUSD = 0,
  miscINR = 0,
  bcdPct = 0,
  swsPct = 10,
  igstPct = 18,
  cifFreightInAssessable = false,
}) {
  const s = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0);
  const usd = s(unitCostUSD);
  const q = s(qty);
  const fx = s(customsFx) || 1;
  const frt = s(freightUSD);
  const ins = s(insuranceUSD);
  const add = s(additionsUSD);
  const misc = s(miscINR);

  // Compute assessable (duty base)
  let assessable;
  if (cifFreightInAssessable) {
    // Template 2: CIF inclusive
    assessable = (usd + frt + ins) * q * fx;
  } else {
    // Template 1: goods only
    assessable = usd * q * fx;
  }

  const bcd = assessable * bcdPct / 100;
  const sws = bcd * swsPct / 100;
  const igstBase = assessable + bcd + sws;
  const igst = igstBase * igstPct / 100;
  const totalDuty = bcd + sws + igst;

  // Landed total
  let landedTotal;
  if (cifFreightInAssessable) {
    // Template 2: misc only added after duty (freight already in assessable)
    landedTotal = assessable + totalDuty + misc;
  } else {
    // Template 1: freight/ins/add added after duty
    landedTotal = assessable + totalDuty + (frt + ins + add) * fx + misc;
  }

  const landedUnit = q > 0 ? landedTotal / q : 0;

  return {
    assessableINR: r2(assessable),
    bcdINR: r2(bcd),
    swsINR: r2(sws),
    igstBaseINR: r2(igstBase),
    igstINR: r2(igst),
    totalDutyINR: r2(totalDuty),
    landedTotalINR: r2(landedTotal),
    landedUnitINR: r2(landedUnit),
    // Unrounded internal values preserved for downstream scenario math precision
    _landedUnitRaw: landedUnit,
    _landedTotalRaw: landedTotal,
  };
}

/**
 * Compute sell price given a cost and margin percentage.
 * Formula: price = cost / (1 - marginPct/100)
 */
export function computeSellPriceFromMargin({ unitCost = 0, marginPct = 0 }) {
  if (marginPct >= 100) return Infinity;
  if (marginPct <= 0) return r2(unitCost);
  return r2(unitCost / (1 - marginPct / 100));
}

/**
 * Compute three margin scenario sell prices for a line.
 * Pass `_landedUnitRaw` from computeLandedCostINR for best precision.
 */
export function computeThreeScenarios({ landedUnit = 0, _landedUnitRaw, qty = 0, minPct = 0, targetPct = 0, stretchPct = 0 }) {
  const lu = _landedUnitRaw !== undefined ? _landedUnitRaw : landedUnit;

  const minSellRaw = lu / (1 - minPct / 100);
  const targetSellRaw = lu / (1 - targetPct / 100);
  const stretchSellRaw = lu / (1 - stretchPct / 100);

  return {
    minSellUnit: r2(minSellRaw),
    targetSellUnit: r2(targetSellRaw),
    stretchSellUnit: r2(stretchSellRaw),
    minLineTotal: r2(minSellRaw * qty),
    targetLineTotal: r2(targetSellRaw * qty),
    stretchLineTotal: r2(stretchSellRaw * qty),
  };
}

/**
 * Two-layer margin pricing for global landed-cost (Template 2).
 * Formula: quote = landedCost / ((1 - coreMarginPct/100) × (1 - miscMarginPct/100))
 */
export function computeRecommendedQuoteTwoLayer({ landedCost = 0, coreMarginPct = 0, miscMarginPct = 0 }) {
  const denom = (1 - coreMarginPct / 100) * (1 - miscMarginPct / 100);
  if (denom <= 0) return Infinity;
  return r2(landedCost / denom);
}

/**
 * Reverse-margin split: given a known selling price and landed cost, derive
 * how much of the margin is core vs misc.
 */
export function reverseMarginSplit({ sellingPrice = 0, landedCost = 0, coreMarginPct = 0, miscMarginPct = 0 }) {
  const totalMargin = r2(sellingPrice - landedCost);
  const recommendedQuote = computeRecommendedQuoteTwoLayer({ landedCost, coreMarginPct, miscMarginPct });

  let coreMarginEarned, miscMarginEarned;
  const coreFactor = 1 - coreMarginPct / 100;
  if (coreFactor <= 0) {
    coreMarginEarned = totalMargin;
    miscMarginEarned = 0;
  } else {
    const intermedPost = landedCost / coreFactor;
    const corePartAtRec = intermedPost - landedCost;
    const miscPartAtRec = recommendedQuote - intermedPost;
    const totalAtRec = corePartAtRec + miscPartAtRec;

    if (totalAtRec <= 0) {
      coreMarginEarned = 0;
      miscMarginEarned = 0;
    } else {
      const coreRaw = totalMargin * (corePartAtRec / totalAtRec);
      coreMarginEarned = r2(coreRaw);
      miscMarginEarned = r2(totalMargin - coreMarginEarned);
    }
  }

  const isFeasible = sellingPrice >= recommendedQuote - 0.01;
  const deltaPct = recommendedQuote > 0 ? r2(((sellingPrice - recommendedQuote) / recommendedQuote) * 100) : 0;

  return { totalMargin, coreMarginEarned, miscMarginEarned, isFeasible, deltaPct };
}
